/**
 * Small UI primitives for the tier system:
 *
 *   <TierBadge />        — shows the user's current tier (Starter / Pro / Pro+ / …)
 *   <FeatureGate>        — renders children only if the user has the named feature
 *   <UpgradePromptBanner> — auto-shows when the user is on a tier that has a
 *                          higher one available, at the top of relevant pages
 */
import React, { useState } from 'react';
import { Sparkles, Lock, ArrowRight } from 'lucide-react';
import { useEntitlements } from '../../hooks/useEntitlements';
import { ReserveProSpotModal } from './ReserveProSpotModal';

const TIER_STYLE = {
  'clinician.starter': { label: 'Starter', cls: 'bg-cn-bg text-cn-text-muted border-cn-border' },
  'clinician.pro':     { label: 'Pro',     cls: 'bg-cn-primary/10 text-cn-primary border-cn-primary/30' },
  'clinician.pro_plus': { label: 'Pro+',   cls: 'bg-gradient-to-r from-cn-primary to-cn-accent text-white border-transparent' },
  'family.basic':      { label: 'Basic',   cls: 'bg-cn-bg text-cn-text-muted border-cn-border' },
  'family.remote_caregiver': { label: 'Remote Caregiver', cls: 'bg-cn-primary/10 text-cn-primary border-cn-primary/30' },
};

export function TierBadge({ className = '' }) {
  const { entitlements } = useEntitlements();
  if (!entitlements?.tier_id) return null;
  const meta = TIER_STYLE[entitlements.tier_id] || { label: entitlements.tier_name || 'Member', cls: 'bg-cn-bg text-cn-text-muted border-cn-border' };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold tracking-wider border ${meta.cls} ${className}`}
      data-testid={`tier-badge-${entitlements.tier_id}`}
    >
      {entitlements.tier_id.includes('pro') && <Sparkles className="w-3 h-3" />}
      {meta.label}
    </span>
  );
}

/**
 * Wrap any tier-locked UI:
 *   <FeatureGate feature="instant_payouts" fallback={<UpgradeStub />}>
 *     <InstantPayoutButton />
 *   </FeatureGate>
 */
export function FeatureGate({ feature, children, fallback = null, locked }) {
  const { hasFeature, loading } = useEntitlements();
  if (loading) return null;
  if (!hasFeature(feature)) {
    if (typeof locked === 'function') return locked();
    return fallback;
  }
  return children;
}

/**
 * A drop-in "Upgrade to Pro" inline locked-state with reserve CTA. Useful
 * as a `fallback` for FeatureGate.
 */
export function FeatureLockedTile({ title, hint, planId = 'clinician.pro' }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="cn-card p-5 border-dashed border-2 border-cn-border/70 flex items-center gap-4" data-testid={`locked-${planId}-${title?.toLowerCase().replace(/\W+/g, '-') || 'tile'}`}>
      <div className="w-11 h-11 rounded-2xl bg-cn-bg flex items-center justify-center text-cn-text-muted shrink-0">
        <Lock className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-cn-text">{title}</p>
        {hint && <p className="text-xs text-cn-text-muted mt-0.5">{hint}</p>}
      </div>
      <button
        onClick={() => setOpen(true)}
        className="cn-btn cn-btn-ghost !py-1.5 !px-3 !text-xs whitespace-nowrap"
        data-testid="feature-gate-upgrade-btn"
      >
        Reserve <ArrowRight className="w-3 h-3" />
      </button>
      <ReserveProSpotModal
        open={open}
        onClose={() => setOpen(false)}
        plan={{ id: planId, name: planId === 'clinician.pro_plus' ? 'Pro+' : 'Pro', audience: 'clinician', auto_upgrade_threshold_usd: 2000 }}
      />
    </div>
  );
}

/**
 * The auto-upgrade-prompt banner. Shows the user when their next-tier has
 * a feature they'd benefit from. Pure cosmetic right now — we evaluate
 * the "is this clinician earning enough to benefit from Pro" math entirely
 * from `entitlements.auto_upgrade_threshold_usd` + the optional
 * `monthly_billings` prop a host page passes in (e.g. ClinicianPayouts).
 */
export function UpgradePromptBanner({ monthlyBillings, className = '' }) {
  const { entitlements } = useEntitlements();
  const [open, setOpen] = useState(false);
  if (!entitlements || !entitlements.can_upgrade) return null;
  if (!entitlements.suggested_tier_id) return null;
  if (entitlements.tier_audience !== 'clinician') return null;

  // Only nudge the user once they're realistically close to the threshold.
  const threshold = entitlements.auto_upgrade_threshold_usd ?? 0;
  const billings = monthlyBillings ?? 0;
  if (threshold && billings < threshold * 0.6) return null; // <60% of threshold = too early

  const target = entitlements.suggested_tier_id;
  const targetLabel = target === 'clinician.pro' ? 'Pro ($49/mo · 8% service rate)'
    : target === 'clinician.pro_plus' ? 'Pro+ ($149/mo · 5% service rate)'
    : 'the next tier';

  return (
    <>
      <div className={`cn-card p-4 sm:p-5 border-cn-primary/30 bg-gradient-to-r from-cn-primary/8 to-transparent flex flex-col sm:flex-row sm:items-center gap-4 ${className}`} data-testid="upgrade-prompt-banner">
        <div className="w-11 h-11 rounded-2xl bg-cn-primary/15 text-cn-primary flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-cn-text">You're billing enough to benefit from {targetLabel}.</p>
          <p className="text-xs text-cn-text-muted mt-0.5">
            Reserve a spot now — no charge today, automatic enrollment when paid billing goes live.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="cn-btn cn-btn-primary !py-2 !px-4 !text-xs shrink-0"
          data-testid="upgrade-prompt-cta"
        >
          Reserve <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <ReserveProSpotModal
        open={open}
        onClose={() => setOpen(false)}
        plan={{ id: target, name: target === 'clinician.pro_plus' ? 'Pro+' : 'Pro', audience: 'clinician', auto_upgrade_threshold_usd: threshold }}
      />
    </>
  );
}
